import {ActionCreatorWithPayload, Dispatch} from '@reduxjs/toolkit';
import axios								from 'axios';

import {DeviceType}							from './typescript-declaration/global';
import {DevicesState, PutDeviceData} 		from '../redux/devices/interface';
import {LoginData, PutUsersOffboarding,
	User} 									from '../redux/user/interface';
import {Document, UploadPercentState}		from '../redux/documents/interface';
import {PostUserData, PutUserData}			from '../redux/companyUsers/interface';
import {PostCompanyData, PutCompanyData}	from '../redux/company/interface';
import {PutAddressData}						from '../redux/address/interface';
import {PostContactNotValalaState}			from '../redux/contractsNotValala/interface';
import {PostOrderData}						from '../redux/order/interface';
import {PostSupportData}					from '../redux/support/interface';
import {Filters}							from '../redux/catalog/interface';
import {ApiContractState}					from '../redux/contracts/interface';
import {ApiMdmLicenseFormState, ApiMdmPostConnect,
	PutMdmDeviceCommand}					from '../redux/devices/interfaceMdm';
import {BasketTracking}						from '../redux/basket/interface';
import {PutCompanyBundleData}				from '../redux/companyBundle/interface';

const APIURLROOT = import.meta.env.VITE_API || 'http://localhost:3020';

type Query = null | Record<string, (boolean | string)>
type Upload_dispatch = {
	index: number,
	dispatch: Dispatch
	function: ActionCreatorWithPayload<UploadPercentState, 'documents/setUploadPercent'>
}

interface ApiSearchData {
	search: string,
	limit?: number
}

interface PutUserPasswordData {
	password: string,
	newPassword: string
}


const APIFETCH = (method: string, link: string, data: (object | null) = null, user: (User | null) = null, query: Query = null, companyToken?: string) => {
	const headers: HeadersInit = {
		'Content-Type': 'application/json'
	};

	if (user) {
		headers.user = user.email;
		headers.token = user.token;
	} else if (companyToken)
		headers.token = companyToken;

	const options: RequestInit = {headers, method};

	if (data)
		options.body = JSON.stringify(data);

	if (query) {
		const params = new URLSearchParams();

		Object.entries(query).forEach(([key, value]) => {
			if (value === undefined || value === null || value === '')
				return;
			params.append(key, String(value));
		});

		const queryString = params.toString();

		if (queryString)
			link += `?${queryString}`;
	}

	return (
		fetch(`${APIURLROOT}${link}`, options)
		.then((response) => {
			if (response.status === 429)
				return {error: 'Too many request'};
			if (response.status === 401)
				return {error: 'Unauthorized'};
			return response.json();
		})
		.then((json) => {
			if (json)
				return json;
			return {error: 'no response'};
		})
		.catch((err) => {
			console.warn(err);
			return {error: 'no response'};
		})
	);
};

const UPLOAD = (link: string, file: File, user: User, query: Query = null, dispatch?: Upload_dispatch, data?: object) => {
	if (query) {
		let	first = true;

		Object.entries(query).forEach((dataQuery) => {
			if (!dataQuery || !dataQuery[0] || !dataQuery[1])
				return;
			link += `${first ? '?' : '&'}${dataQuery[0]}=${dataQuery[1]}`;
			first = false;
		});
	}

	const formData = new FormData();

	function objectToFormData(obj: object, parentKey?: string) {
		Object.entries(obj).forEach(([key, value]) => {
			const formKey = parentKey ? `${parentKey}.${key}` : key;

			if (value && typeof value === 'object' && !(value instanceof File) && !(value instanceof Date))
				objectToFormData(value, formKey);
			else
				formData.append(formKey, value);
		});
	}

	if (data)
		objectToFormData(data);

	formData.append('file', file, file.name);

	const config = {
		headers: {
			user:           user?.email,
			token:          user?.token,
			'Content-Type': 'multipart/form-data'
		},
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		onUploadProgress: (progressEvent: any) => {
			const percent = Math.round((progressEvent.loaded / progressEvent.total) * 100);
			console.log(`Upload progress: ${percent}%`);

			if (dispatch) {
				dispatch.dispatch(dispatch.function({
					percent,
					index: dispatch.index
				}));
			}
		}
	};

	return (
		axios.post(`${APIURLROOT}${link}`, formData, config)
		.then((response) => {
			console.log('Upload complete!');
			return response?.data;
		})
		.catch((err) => {
			console.warn(err);
			return {error: 'no response'};
		})
	);
};


const DOWNLOAD = (method: string, link: string, data: (object | null) = null, name: string, user: User, query: Query = null, returnUrl = false) => {
	const headers: HeadersInit = {
		'Content-Type': 'application/json'
	};

	if (user) {
		headers.user = user.email;
		headers.token = user.token;
	}

	const options: RequestInit = {headers, method};

	if (data)
		options.body = JSON.stringify(data);

	if (query) {
		let	first = true;

		Object.entries(query).forEach((dataQuery) => {
			if (!dataQuery || !dataQuery[0] || !dataQuery[1])
				return;
			link += `${first ? '?' : '&'}${dataQuery[0]}=${dataQuery[1]}`;
			first = false;
		});
	}

	return (
		fetch(`${APIURLROOT}${link}`, options)
		.then(resp => resp.blob())
		.then((blob) => {
			if (blob.type === 'application/json') {
				return new Promise((resolve) => {
					const reader = new FileReader();
					reader.onload = () => {
						try {
							if (typeof reader.result === 'string')
								resolve(JSON.parse(reader.result));
							else
								resolve(blob);
						} catch (e) {
							resolve(blob);
						}
					};
					reader.readAsText(blob);
				});
			}

			const url = window.URL.createObjectURL(blob);

			if (returnUrl)
				return url;

			const a = document.createElement('a');

			a.style.display = 'none';
			a.href = url;
			a.download = name;

			document.body.appendChild(a);
			a.click();
			return ({status: true});
		})
		.catch((err) => {
			console.log(err);
			return ({status: false});
		})
	);
};

// --- User
export const login = (data: LoginData) => APIFETCH('POST', '/user/login', data);
export const loginConfirm = (user: User) => APIFETCH('GET', '/user/login/confirm', null, user);
export const forgetPassword = (email: string) => APIFETCH('PUT', '/public/user/passwordForget', {email});
export const forgetPasswordReset = (id: string, token: string, password: string) => APIFETCH('PUT', '/public/user/passwordForget/edit', {id, token, password});
export const initPassword = (users: string[], user: User) => APIFETCH('POST', '/user/password/init', {users}, user);

export const initMFA = (user: User) => APIFETCH('GET', '/user/mfa/init', null, user);
export const validateMFA = (token: string, user: User) => APIFETCH('POST', '/user/mfa/init', {token}, user);

export const checkEmailUse = (email: string) => APIFETCH('GET', `/public/user/${email}`);

export const getUsers = (user: User) => APIFETCH('GET', '/user', null, user);
export const postUser = (data: PostUserData, user: User) => APIFETCH('POST', '/user', data, user);
export const putUser = (idUser: string, data: PutUserData, user: User) => APIFETCH('PUT', `/user/${idUser}`, data, user);
export const putUserPassword = (data: PutUserPasswordData, user: User) => APIFETCH('PUT', '/user/password/edit', data, user);
export const deleteUser = (idUser: string, user: User) => APIFETCH('DELETE', `/user/${idUser}`, null, user);
export const putUsersOffboarding = (data: PutUsersOffboarding, user: User) => APIFETCH('PUT', '/users/offboarding', data, user);

export const getUsersTags = (user: User) => APIFETCH('GET', '/ressources/user/tags', null, user);
export const getUserOauth = (user: User, withEstablishments = false) => APIFETCH('GET', '/ressources/user/oauth', null, user, {withEstablishments});
export const postHirs = (hrisName: string, data: {url: string, token: string}, user: User) => APIFETCH('POST', `/ressources/user/hris/${hrisName}`, data, user);
export const putHirs = (hrisName: string, data: {establishments: string[]}, user: User) => APIFETCH('PUT', `/ressources/user/hris/${hrisName}`, data, user);
export const importUserHris = (hrisName: string, user: User) => APIFETCH('GET', `/ressources/user/hris/${hrisName}`, null, user);

export const uploadProfilePicture = (idUser: string, file: File, user: User) => UPLOAD(`/user/${idUser}/profilePicture/upload`, file, user);
export const getUsersDocuments = (idUser: string, user: User) => APIFETCH('GET', `/documents/user/${idUser}`, null, user);
export const uploadUsersDocument = (idUser: string, file: File, user: User, dispatch: Upload_dispatch) => UPLOAD(`/documents/user/${idUser}`, file, user, null, dispatch);
export const deleteUsersDocument = (idUser: string, file: Document, user: User) => APIFETCH('DELETE', `/documents/user/${idUser}/${file._id}`, null, user);
export const downloadUsersDocument = (idUser: string, file: Document, user: User, returnUrl?: boolean) => DOWNLOAD('GET', `/documents/user/${idUser}/download/${file._id}`, null, file.name, user, undefined, returnUrl);

export const switchCompany = (idCompany: string, user: User) => APIFETCH('PUT', '/user/company/switch', {company: idCompany}, user);
export const postUserPublic = (data: PostUserData, token: string) => APIFETCH('POST', '/public/user', data, null, null, token);

// --- Device
export const getDevices = (user: User, withContract: boolean) => APIFETCH('GET', '/device', null, user, withContract ? {allData: true} : null);
export const postDevice = (data: PutDeviceData, user: User) => APIFETCH('POST', '/device', data, user);
export const putDevice = (idDevice: string, data: PutDeviceData, user: User) => APIFETCH('PUT', `/device/${idDevice}`, data, user);
export const deleteDevice = (idDevice: string, user: User) => APIFETCH('DELETE', `/device/${idDevice}`, null, user);

export const getDevicesTags = (user: User) => APIFETCH('GET', '/ressources/device/tags', null, user);

export const getDeviceDocuments = (idDevice: string, user: User) => APIFETCH('GET', `/documents/device/${idDevice}`, null, user);
export const uploadDeviceDocument = (idDevice: string, file: File, user: User, dispatch: Upload_dispatch) => UPLOAD(`/documents/device/${idDevice}`, file, user, null, dispatch);
export const deleteDeviceDocument = (idDevice: string, file: Document, user: User) => APIFETCH('DELETE', `/documents/device/${idDevice}/${file._id}`, null, user);
export const downloadDeviceDocument = (idDevice: string, file: Document, user: User, returnUrl?: boolean) => DOWNLOAD('GET', `/documents/device/${idDevice}/download/${file._id}`, null, file.name, user, undefined, returnUrl);

export const uploadDeviceImportDocument = (file: File, user: User) => UPLOAD(`/documents/company-upload/${user?.company}`, file, user);

export const getMobileLine = (user: User) => APIFETCH('GET', '/mobileLines', null, user);
export const getMobileLineUsage = (phoneNumber: string, serial: string, user: User) => APIFETCH('GET', '/mobileLine/usage', undefined, user, {phoneNumber, serial});
// --- MDM
export const getMdm = (user: User) => APIFETCH('GET', '/mdm', null, user);
export const postMdm = (mdmName: keyof DevicesState['mdm'], data: ApiMdmPostConnect, user: User) => APIFETCH('POST', `/mdm/${mdmName}`, data, user);

export const getMdmDevices = (mdmName: keyof DevicesState['mdm'], user: User) => APIFETCH('GET', `/mdm/${mdmName}/devices`, null, user);
export const getMdmDeviceAllDataJamf = (id: string, serial: string, type: string, user: User) => APIFETCH('GET', `/mdm/jamf/device/${type}/${id}/${serial}`, null, user);
export const getMdmDeviceSpecificDataJamf = (dataType: 'accounts' | 'applications' | 'os', id: string, serial: string, type: string, user: User) => APIFETCH('GET', `/mdm/jamf/device/${type}/${id}/${serial}/${dataType}`, null, user);
export const getMdmDeviceAccountsVmWare = (serial: string, user: User) => APIFETCH('GET', `/mdm/vmWare/device/${serial}/accounts`, null, user);
export const getMdmDeviceJamfProtect = (serial: string, user: User) => APIFETCH('GET', `/mdm/jamfProtect/device/${serial}`, null, user);
export const putMdmDeviceCommand = (mdmName: keyof DevicesState['mdm'], data: PutMdmDeviceCommand, isMobile: boolean, user: User) => APIFETCH('PUT', `/mdm/${mdmName}/device/command`, {...data, isMobile}, user);

export const getMdmDeviceGroupsJamf = (isMobile: boolean, user: User) => APIFETCH('GET', '/mdm/jamf/groups', null, user, {isMobile});
export const getMdmDeviceGroupsByIdJamf = (id: string, isMobile: boolean, user: User) => APIFETCH('GET', `/mdm/jamf/groups/${id}`, null, user, {isMobile});
export const putMdmDeviceGroupsJamf = (id: string[], data: ({idAdd: string} | {idRemove: string}), isMobile: boolean, user: User) => APIFETCH('PUT', `/mdm/jamf/groups/${id}`, data, user, {isMobile});

export const postMdmInvitations = (mdmName: keyof DevicesState['mdm'], users: string[], user: User) => APIFETCH('POST', `/mdm/${mdmName}/invitations`, {users}, user);

export const postMdmLicenseForm = (data: ApiMdmLicenseFormState, user: User) => APIFETCH('POST', '/mdm/licenceForm', data, user);

export const getMdmHistory = (mdmName: keyof DevicesState['mdm'], user: User) => APIFETCH('GET', `/mdm/${mdmName}/history`, null, user);

// --- Contract
export const getContracts = (user: User) => APIFETCH('GET', '/contract', null, user);
export const downloadContract = (idContact: string, file: Document, user: User, returnUrl?: boolean) => DOWNLOAD('GET', `/contract/${idContact}/download/${file._id}`, null, file.name, user, undefined, returnUrl);
export const putContract = (idContract: string, data: ApiContractState, user: User) => APIFETCH('PUT', `/contract/${idContract}/basic`, data, user);

export const getContractsNotValala = (user: User) => APIFETCH('GET', '/contract/notValala', null, user);
export const postContractNotValala = (data: PostContactNotValalaState, user: User) => APIFETCH('POST', '/contract/notValala', data, user);
export const putContractNotValala = (idContract: string, data: PostContactNotValalaState, user: User) => APIFETCH('PUT', `/contract/notValala/${idContract}`, data, user);

export const getContractNotValalaDocument = (idContract: string, user: User) => APIFETCH('GET', `/documents/contractNotValala/${idContract}`, null, user);
export const uploadContractNotValalaDocument = (idContract: string, file: File, user: User, dispatch: Upload_dispatch) => UPLOAD(`/documents/contractNotValala/${idContract}`, file, user, null, dispatch);
export const downloadContractNotValalaDocument = (idContract: string, file: Document, user: User, returnUrl?: boolean) => DOWNLOAD('GET', `/documents/contractNotValala/${idContract}/download/${file._id}`, null, file.name, user, undefined, returnUrl);
export const deleteContractNotValalaDocument = (idContract: string, file: Document, user: User) => APIFETCH('DELETE', `/documents/contractNotValala/${idContract}/${file._id}`, null, user);

export const getContractsTags = (user: User) => APIFETCH('GET', '/ressources/contract/tags', null, user);

// --- Catalog (Shop)
export const getCatalog = (deviceType: DeviceType | undefined, user: User) => APIFETCH('GET', '/public/shop', null, user, {type: deviceType || ''});
export const getCatalogById = (idCatalog: string, user: User) => APIFETCH('GET', `/public/shop/${idCatalog}`, null, user);
export const getCatalogOption = (idCatalog: string, user: User) => APIFETCH('GET', `/public/shop/${idCatalog}/options`, null, user);
export const getCatalogFiltered = (data: Filters, user: User) => APIFETCH('SEARCH', '/public/shop/filter', data, user);

export const searchCatalogCategorieByName = (data: ApiSearchData, user: User) => APIFETCH('POST', '/shopCategory/searchByName', data, user);
export const getCatalogCategorieFirstDevice = (catalogId: string, user: User) => APIFETCH('GET', `/shopCategory/${catalogId}/device`, null, user);
export const getCatalogFilters = (deviceType?: DeviceType) => APIFETCH('GET', `/public/shop/filter/${deviceType || 'global'}?withBrand=true`);

export const getCatalogFavorite = (user: User) => APIFETCH('GET', '/user/favorite/shop', null, user);
export const putCatalogFavorite = (idShop: string, user: User) => APIFETCH('PUT', `/user/favorite/shop/${idShop}`, null, user);
export const deleteCatalogFavorite = (idShop: string, user: User) => APIFETCH('DELETE', `/user/favorite/shop/${idShop}`, null, user);

export const getCompanyBundle = (user: User) => APIFETCH('GET', '/shop/company/bundle', null, user);
export const postCompanyBundle = (data: PutCompanyBundleData, user: User) => APIFETCH('POST', '/shop/company/bundle', data, user);
export const putCompanyBundle = (idBundle: string, data: PutCompanyBundleData, user: User) => APIFETCH('PUT', `/shop/company/bundle/${idBundle}`, data, user);
export const deleteCompanyBundle = (idBundle: string, user: User) => APIFETCH('DELETE', `/shop/company/bundle/${idBundle}`, null, user);

// --- Company
export const getCompany = (user: User) => APIFETCH('GET', `/company/${user?.company}`, null, user);
export const getCompanyHideOauth = (user: User) => APIFETCH('GET', `/company/${user?.company}?hideOauth=true`, null, user);
export const putCompany = (idCompany: string, data: PutCompanyData, user: User) => APIFETCH('PUT', `/company/${idCompany}`, data, user);

export const postCompany = (data: PostCompanyData) => APIFETCH('POST', '/public/company', data);
export const checkSiretUse = (siret: string) => APIFETCH('GET', `/public/company/${siret}`);

export const getCompanyDocuments = (user: User) => APIFETCH('GET', `/documents/company/${user?.company}`, null, user);
export const uploadCompanyDocument = (type: string, file: File, user: User, dispatch: Upload_dispatch, data?: object) => UPLOAD(`/documents/company/${user?.company}/${type}`, file, user, null, dispatch, data);
export const uploadCompanyDocumentOrderToken = (type: string, file: File, orderId: string, orderToken: string, dispatch: Upload_dispatch, data?: object) => UPLOAD(`/public/documents/company/${type}/${orderId}/${orderToken}`, file, null, null, dispatch, data);
export const downloadCompanyDocument = (idCompany: string, file: Document, user: User, returnUrl?: boolean) => DOWNLOAD('GET', `/documents/company/${idCompany}/download/${file._id}`, null, file.name, user, undefined, returnUrl);
export const deleteCompanyDocument = (idCompany: string, file: Document, user: User) => APIFETCH('DELETE', `/documents/company/${idCompany}/${file._id}`, null, user);

// --- Address
export const getAddress = (user: User) => APIFETCH('GET', '/address', null, user);
export const postAddress = (data: PutAddressData, user: User) => APIFETCH('POST', '/address', data, user);
export const putAddress = (idAddress: string, data: PutAddressData, user: User) => APIFETCH('PUT', `/address/${idAddress}`, data, user);
export const setMainAddress = (idAddress: string, dataName: 'main' | 'billingMain', user: User) => APIFETCH('PUT', `/address/${idAddress}/${dataName}`, {[dataName]: true}, user);
export const deleteAddress = (idAddress: string, user: User) => APIFETCH('DELETE', `/address/${idAddress}`, null, user);

// --- Order
export const getOrder = (user: User) => APIFETCH('GET', '/order', null, user);
export const getOrderLimited = (idOrder: string, orderToken: string) => APIFETCH('GET', `/public/order/${idOrder}/${orderToken}`);
export const putOrderAssignDevice = (idOrder: string, indexAddress: string, indexDevice: string, indexAssign: string, idUser: string, user: User) => APIFETCH('PUT', `/order/${idOrder}/${indexAddress}/${indexDevice}/assign/${indexAssign}`, {user: idUser}, user);
export const postOrder = (data: PostOrderData, user: User) => APIFETCH('POST', '/order', data, user);

// --- Support
export const getSupport = (user: User) => APIFETCH('GET', '/support', null, user);
export const postSupport = (data: PostSupportData, user: User) => APIFETCH('POST', '/support', data, user);
export const getSupportMessages = (idSupport: string, user: User) => APIFETCH('GET', `/support/${idSupport}`, null, user);
export const postSupportMessages = (idSupport: string, data: PostSupportData, user: User) => APIFETCH('POST', `/support/${idSupport}`, data, user);
export const closeSupport = (idSupport: string, user: User) => APIFETCH('PUT', `/support/${idSupport}/close`, null, user);
export const noticeSupport = (idSupport: string, data: PostSupportData, user: User) => APIFETCH('PUT', `/support/${idSupport}/notice`, data, user);

export const uploadSupportDocument = (idSupport: string, file: File, user: User, dispatch: Upload_dispatch) => UPLOAD(`/documents/support/${idSupport}`, file, user, null, dispatch);
export const getSupportDocument = (idSupport: string, user: User) => APIFETCH('GET', `/documents/support/${idSupport}`, null, user);
export const downloadSupportDocument = (idSupport: string, file: Document, user: User) => DOWNLOAD('GET', `/documents/support/${idSupport}/download/${file._id}`, null, file.name, user);

// --- Dashboard
export const getDashboard = (user: User) => APIFETCH('GET', '/dashboard', null, user);
export const getDashboardNotification = (user: User) => APIFETCH('GET', '/dashboard/notification', null, user);
export const closeDashboardNotification = (idNotification: string, user: User) => APIFETCH('PUT', `/dashboard/notification/${idNotification}`, null, user);

// --- Tracking
export const putCartTracking = (cartData: BasketTracking, user: User) => APIFETCH('PUT', '/cart/saas', cartData, user);